import jsPDF from "jspdf";
import { LOGO_RELATORIO_PADRAO, NOME_CLIENTE_DOCUMENTO } from "../Config/branding";

const azul = [20, 77, 120];
const azulClaro = [207, 229, 247];

function valor(conteudo) {
  return String(conteudo ?? "").trim() || "-";
}

function formatarData(data) {
  const [ano, mes, dia] = String(data || "").split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor(data);
}

function formatarRevisao(revisao) {
  const numero = String(revisao ?? "").trim();
  return numero ? numero.padStart(2, "0") : "";
}

export function nomePdfPlanejamento(registro) {
  return `RQ-1.4-PLANEJAMENTO-ESTRATEGICO-REV-${formatarRevisao(registro?.revisao)}.pdf`;
}

function carregarImagem(url) {
  return new Promise((resolve, reject) => {
    const imagem = new Image();
    imagem.crossOrigin = "anonymous";
    imagem.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = imagem.naturalWidth;
        canvas.height = imagem.naturalHeight;
        canvas.getContext("2d").drawImage(imagem, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          largura: imagem.naturalWidth,
          altura: imagem.naturalHeight,
        });
      } catch (error) {
        reject(error);
      }
    };
    imagem.onerror = reject;
    imagem.src = url;
  });
}

async function carregarLogoRelatorio() {
  const tenantId = String(localStorage.getItem("tenantId") || "").trim();
  const logoConfigurada =
    localStorage.getItem(`logoSistemaUrl:${tenantId}`) ||
    localStorage.getItem("logoRelatorioUrl") ||
    LOGO_RELATORIO_PADRAO;

  try {
    return await carregarImagem(logoConfigurada);
  } catch (error) {
    if (logoConfigurada === LOGO_RELATORIO_PADRAO) return null;
    try {
      return await carregarImagem(LOGO_RELATORIO_PADRAO);
    } catch (erroLogoPadrao) {
      return null;
    }
  }
}

export async function gerarPdfPlanejamentoEstrategico(registro) {
  const logo = await carregarLogoRelatorio();
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const margem = 18;
  const largura = 210 - margem * 2;
  const limiteInferior = 278;
  let y = 31;

  function cabecalho() {
    pdf.setDrawColor(...azul);
    pdf.setLineWidth(0.35);
    pdf.line(margem, 23, 210 - margem, 23);
    pdf.setTextColor(...azul);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text("Planejamento Estratégico", 105, 12, { align: "center" });
    pdf.setFontSize(9);
    pdf.text(NOME_CLIENTE_DOCUMENTO.toUpperCase(), 105, 17, { align: "center" });
    pdf.setFontSize(8);
    if (logo) {
      const larguraMaxima = 32;
      const alturaMaxima = 14;
      const proporcao = Math.min(larguraMaxima / logo.largura, alturaMaxima / logo.altura);
      const larguraLogo = logo.largura * proporcao;
      const alturaLogo = logo.altura * proporcao;
      pdf.addImage(logo.dataUrl, "PNG", margem, 5 + (alturaMaxima - alturaLogo) / 2, larguraLogo, alturaLogo);
    }
    pdf.text("RQ 1.4", 210 - margem, 10, { align: "right" });
    pdf.text(`Revisão ${formatarRevisao(registro.revisao)}`, 210 - margem, 15, { align: "right" });
    pdf.text(formatarData(registro.dataRevisao), 210 - margem, 20, { align: "right" });
    pdf.setTextColor(23, 32, 51);
  }

  function novaPagina() {
    pdf.addPage();
    cabecalho();
    y = 31;
  }

  function garantirEspaco(altura) {
    if (y + altura > limiteInferior) novaPagina();
  }

  function tituloSecao(titulo) {
    garantirEspaco(10);
    pdf.setFillColor(...azulClaro);
    pdf.rect(margem, y, largura, 7, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(...azul);
    pdf.text(titulo, margem + 2, y + 4.8);
    pdf.setTextColor(23, 32, 51);
    y += 10;
  }

  function texto(conteudo, titulo = "") {
    if (titulo) {
      garantirEspaco(8);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      pdf.text(titulo, margem, y);
      y += 5;
    }
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    const paragrafos = valor(conteudo).split(/\n+/);
    paragrafos.forEach((paragrafo) => {
      const linhas = pdf.splitTextToSize(paragrafo || " ", largura);
      linhas.forEach((linha) => {
        garantirEspaco(5);
        pdf.text(linha, margem, y);
        y += 4.2;
      });
      y += 1.2;
    });
    y += 1;
  }

  cabecalho();
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(28);
  pdf.setTextColor(...azul);
  pdf.text("PLANEJAMENTO", 105, 105, { align: "center" });
  pdf.text("ESTRATÉGICO", 105, 118, { align: "center" });
  pdf.setFontSize(13);
  pdf.text(NOME_CLIENTE_DOCUMENTO.toUpperCase(), 105, 145, { align: "center" });
  pdf.setTextColor(23, 32, 51);

  novaPagina();
  tituloSecao("Apresentação");
  texto(registro.apresentacao);

  tituloSecao("1. Identidade Organizacional");
  texto(registro.politicaQualidade, "Política da Qualidade");
  texto(registro.missao, "Missão");
  texto(registro.visao, "Visão");
  texto(registro.valores, "Valores");

  tituloSecao("2. Análise do Contexto");
  texto(registro.oportunidades, "Ambiente Externo - Oportunidades");
  texto(registro.ameacas, "Ambiente Externo - Ameaças");
  texto(registro.forcas, "Ambiente Interno - Forças");
  texto(registro.fraquezas, "Ambiente Interno - Fraquezas");

  tituloSecao("3. Partes Interessadas - Necessidades e Expectativas");
  texto(registro.partesInteressadas);

  tituloSecao("4. Gestão dos Riscos");
  texto(registro.gestaoRiscos);

  tituloSecao("5. Objetivos Estratégicos e Metas");
  texto(registro.objetivosEstrategicos);

  tituloSecao("6. Estratégias");
  texto(registro.estrategias);

  tituloSecao("7. Indicadores de Desempenho (KPIs)");
  texto(registro.indicadoresDesempenho);

  tituloSecao("8. Plano de Ação Estratégico (Macro)");
  texto(registro.planoCurtoPrazo, "Curto Prazo");
  texto(registro.planoMedioPrazo, "Médio Prazo");
  texto(registro.planoLongoPrazo, "Longo Prazo");

  tituloSecao("9. Governança e Acompanhamento");
  texto(registro.governancaAcompanhamento);

  tituloSecao("Conclusão");
  texto(registro.conclusao);
  texto(registro.responsaveis, "Responsáveis");
  texto(`${valor(registro.local)} - ${formatarData(registro.dataAssinatura)}`, "Local e data");

  const totalPaginas = pdf.getNumberOfPages();
  for (let pagina = 1; pagina <= totalPaginas; pagina += 1) {
    pdf.setPage(pagina);
    pdf.setDrawColor(40, 40, 40);
    pdf.line(margem, 286, 210 - margem, 286);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.5);
    pdf.setTextColor(70, 70, 70);
    pdf.text("Registro da Qualidade - Sistema de Gestão da Qualidade - Documento Controlado", margem, 291);
    pdf.text(`Pág. ${pagina} de ${totalPaginas}`, 210 - margem, 291, { align: "right" });
  }

  return pdf;
}
